import {
  INTENT_QUESTIONS,
  type BrandingId, type IntentId, type OccasionId, type OrderTopicId, type QtyBandId, type QuestionId,
  type QuestionTopicId, type RecipientId, type TimingId,
} from "./ids";
import type { Draft } from "./state";
import { formatDay } from "./season";

/**
 * Il testo del campo `message`, composto dalle scelte: è ESATTAMENTE ciò che il
 * team riceve (email interna e dashboard, entrambe in `pre-wrap`) e ciò che la
 * cartolina mostra prima dell'invio. Il payload non cambia: le scelte guidate
 * vivono solo qui dentro.
 *
 *   Offerta · Roma ×50, Portofino ×18 · Natale 2026      ← oggetto, ≤ 80 caratteri
 *                                                       (la lista della dashboard ne mostra 80)
 *   Confezioni: Roma ×50, Portofino ×18                  ← una riga per risposta, ordine fisso
 *   Personalizzazione: Logo
 *   Tempi: Natale 2026
 *
 *   Testo libero dell'utente, invariato.
 *
 * Senza intento, o con «Altro», il messaggio è la sola nota: come il modulo di oggi.
 * Testo semplice nella lingua del payload (`locale`): niente HTML, slug o prezzi.
 */

export const MAX_MESSAGE = 5000; // = GB-BACKEND app/schemas/contact.py MAX_MESSAGE (oltre, il server tronca in silenzio)
export const HEADLINE_MAX = 80;  // anteprima della lista richieste in dashboard: message.slice(0, 80)

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

export interface ComposeBox {
  title: string;
  qty: number | null;
}

export interface ComposeInput {
  intent: IntentId | null;
  boxes: ComposeBox[];
  box: string | null;
  qtyBand: QtyBandId | null;
  timing: TimingId | null;
  date: string;
  occasion: OccasionId | null;
  recipients: RecipientId[];
  branding: BrandingId[];
  orderTopic: OrderTopicId | null;
  questionTopics: QuestionTopicId[];
  note: string;
  year: number;
  lang: string;
}

export interface ComposeResult {
  message: string;
  headline: string | null;
  /** La parte generata dalle scelte (oggetto + dettagli), senza la nota. */
  guided: string;
  /** Caratteri disponibili per la nota prima di superare MAX_MESSAGE. */
  noteBudget: number;
}

type Kind = QuestionId;

interface Part {
  kind: Kind;
  items: string[];
  /** Separatore nell'oggetto («Roma + Portofino» per la box su misura); nel corpo sempre «, ». */
  sep: string;
  /** Se può entrare nell'oggetto: «Non so ancora» o «Entro una data» senza data da soli non dicono nulla. */
  headline: boolean;
  dropped: number;
}

/** Minimo d'ordine più basso del catalogo: vale per confezioni di cui il catalogo non dice nulla. */
const FLOOR_QTY = 18;

/** Le risposte, già tradotte, nell'ordine fisso delle righe di dettaglio. */
function answers(input: ComposeInput, t: Translate): Part[] {
  const out: Part[] = [];
  const add = (kind: Kind, items: string[], sep = ", ", headline = true) => {
    const clean = items.map((s) => s.trim()).filter(Boolean);
    if (clean.length) out.push({ kind, items: clean, sep, headline, dropped: 0 });
  };
  add("boxes", input.boxes.map((b) => (b.qty ? `${b.title} ×${b.qty}` : b.title)), input.intent === "custom" ? " + " : ", ");
  if (input.box) add("box", [input.box]);
  if (input.qtyBand) add("qtyBand", [t(`contact.guide.qtyBand.${input.qtyBand}`)], ", ", input.qtyBand !== "qUnknown");
  if (input.occasion) add("occasion", [t(`contact.guide.occasion.${input.occasion}`)]);
  add("recipients", input.recipients.map((r) => t(`contact.guide.recipients.${r}`)));
  add("branding", input.branding.map((b) => t(`contact.guide.branding.${b}`)));
  if (input.timing) {
    const open = input.timing === "date" && !formatDay(input.date, input.lang);
    add("timing", [timingText(input, t)], ", ", !open);
  }
  if (input.orderTopic) add("orderTopic", [t(`contact.guide.orderTopic.${input.orderTopic}`)]);
  add("questionTopics", input.questionTopics.map((q) => t(`contact.guide.questionTopic.${q}`)));
  return out;
}

function timingText(input: ComposeInput, t: Translate): string {
  if (input.timing === "christmas") return t("contact.guide.timing.christmas", { year: input.year });
  if (input.timing === "date") {
    const date = formatDay(input.date, input.lang);
    return date ? t("contact.guide.timing.byDate", { date }) : t("contact.guide.msg.dateOpen");
  }
  return t(`contact.guide.timing.${input.timing}`);
}

/** Quali risposte entrano nell'oggetto, per intento, dalla più importante. */
const HEADLINE_KINDS: Record<IntentId, readonly Kind[]> = {
  offer: ["boxes", "qtyBand", "timing"],
  custom: ["boxes", "qtyBand", "timing"],
  branding: ["branding", "qtyBand", "timing"],
  advice: ["occasion", "qtyBand", "timing"],
  order: ["orderTopic"],
  question: ["box", "questionTopics"],
  other: [],
};

/**
 * Oggetto entro HEADLINE_MAX. In quest'ordine: gli elenchi perdono le ultime
 * voci («+2 altre»), poi si tolgono le parti in coda (prima i tempi, poi la
 * quantità), mai la prima; se ancora non basta resta l'etichetta breve.
 * Così «Natale 2026» resta visibile anche con molte confezioni.
 */
function fitHeadline(label: string, parts: Part[], t: Translate): { text: string; kept: Part[] } {
  const kept = parts.map((p) => ({ ...p, items: [...p.items] }));
  const partText = (p: Part) => p.items.join(p.sep) + (p.dropped ? ` ${t("contact.guide.msg.more", { n: p.dropped })}` : "");
  const render = () => [label, ...kept.map(partText)].join(" · ");
  for (const p of kept) {
    while (render().length > HEADLINE_MAX && p.items.length > 1) {
      p.items.pop();
      p.dropped += 1;
    }
  }
  while (render().length > HEADLINE_MAX && kept.length > 1) kept.pop();
  if (render().length > HEADLINE_MAX) kept.length = 0;
  return { text: render(), kept };
}

export function composeMessage(input: ComposeInput, t: Translate): ComposeResult {
  const note = input.note.replace(/\r\n?/g, "\n").trim();
  if (!input.intent || input.intent === "other") {
    return { message: note, headline: null, guided: "", noteBudget: MAX_MESSAGE };
  }
  const all = answers(input, t);
  const label = t(`contact.guide.intents.${input.intent}.short`);
  const wanted = HEADLINE_KINDS[input.intent];
  const headParts = wanted.flatMap((k) => all.filter((p) => p.kind === k && p.headline));
  const head = fitHeadline(label, headParts, t);
  // Una riga di dettaglio si omette solo se la sua risposta è rimasta INTERA
  // nell'oggetto: deciso per struttura, mai confrontando sottostringhe.
  const intact = new Set(head.kept.filter((p) => p.dropped === 0).map((p) => p.kind));
  const details = all
    .filter((p) => !intact.has(p.kind))
    .map((p) => `${t(`contact.guide.msg.${p.kind}`)}: ${p.items.join(", ")}`)
    .join("\n");
  const guided = [head.text, details].filter(Boolean).join("\n\n");
  const message = [guided, note].filter(Boolean).join("\n\n");
  return { message, headline: head.text, guided, noteBudget: MAX_MESSAGE - guided.length - 2 };
}

/**
 * Dalla bozza (solo id e slug) all'input del compositore, tenendo SOLO le domande
 * pertinenti all'intento: le altre risposte restano nella bozza (tornando indietro
 * ricompaiono) ma non partono.
 */
export function toComposeInput(
  d: Draft,
  catalog: Record<string, { title: string; moq: number | null }> | null,
  year: number,
  lang: string,
): ComposeInput {
  const q: readonly QuestionId[] = d.intent ? INTENT_QUESTIONS[d.intent] : [];
  const has = (k: QuestionId) => q.includes(k);
  // Lookup solo su chiavi proprie: uno slug come «constructor» non legge il prototipo.
  const own = <T,>(rec: Record<string, T> | null | undefined, key: string): T | undefined =>
    rec && Object.prototype.hasOwnProperty.call(rec, key) ? rec[key] : undefined;
  const titleOf = (slug: string) => own(catalog, slug)?.title ?? own(d.titles, slug) ?? null;
  const boxes: ComposeBox[] = has("boxes")
    ? d.boxes.flatMap((slug) => {
        const title = titleOf(slug);
        if (!title) return [];
        let qty: number | null = null;
        if (d.intent === "offer") {
          const n = parseInt(own(d.qty, slug) ?? "", 10);
          const moq = own(catalog, slug)?.moq ?? null;
          // Mai sotto il minimo d'ordine: lo stepper lo corregge all'uscita dal campo,
          // qui si garantisce anche mentre si sta ancora scrivendo. Se il catalogo non
          // conosce la confezione (carrello vecchio) si tiene solo da 18 pezzi in su.
          if (Number.isNaN(n) || n <= 0) qty = null;
          else if (moq) qty = Math.max(n, moq);
          else qty = n >= FLOOR_QTY ? n : null;
        }
        return [{ title, qty }];
      })
    : [];
  return {
    intent: d.intent,
    boxes,
    box: has("box") && d.box ? titleOf(d.box) : null,
    // Nell'offerta la quantità complessiva serve solo se non ci sono confezioni (con i loro pezzi).
    qtyBand: has("qtyBand") && !(d.intent === "offer" && boxes.length > 0) ? d.qtyBand : null,
    timing: has("timing") ? d.timing : null,
    date: has("timing") && d.timing === "date" ? d.date : "",
    occasion: has("occasion") ? d.occasion : null,
    recipients: has("recipients") ? d.recipients : [],
    branding: has("branding") ? d.branding : [],
    orderTopic: has("orderTopic") ? d.orderTopic : null,
    questionTopics: has("questionTopics") ? d.questionTopics : [],
    note: d.note,
    year,
    lang,
  };
}
